import React, { useEffect, useState } from "react";

/**
 * TableOfContents — sticky sidebar on lg+, collapsible block on mobile.
 * Uses IntersectionObserver to highlight the section currently in viewport.
 * Smooth anchor scroll is provided by the global `scroll-behavior: smooth`
 * + the per-heading `scroll-margin-top` set in index.css.
 */
export function TableOfContents({ items }) {
  const [active, setActive] = useState(items[0]?.id || null);

  useEffect(() => {
    if (!items?.length) return undefined;
    const targets = items.map((i) => document.getElementById(i.id)).filter(Boolean);
    if (!targets.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 },
    );
    targets.forEach((t) => obs.observe(t));
    return () => obs.disconnect();
  }, [items]);

  if (!items?.length) return null;

  return (
    <nav aria-label="Table of contents" data-testid="blog-toc">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B7280] mb-4">
        On this page
      </div>
      <ol className="space-y-2.5">
        {items.map((it) => {
          const isActive = it.id === active;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                data-testid={`toc-item-${it.id}`}
                className={
                  "block text-[14px] leading-snug border-l-2 pl-3 -ml-px py-0.5 transition-all duration-200 " +
                  (isActive
                    ? "border-[#0066FF] text-[#0A2540] font-medium"
                    : "border-transparent text-[#6B7280] hover:text-[#0A2540] hover:border-[#D1D5DB]")
                }
              >
                {it.text}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Mobile collapsible variant. Same items, accordion-style trigger. */
export function MobileTableOfContents({ items }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <details
      className="lg:hidden mb-8 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] overflow-hidden"
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="blog-toc-mobile"
    >
      <summary className="cursor-pointer select-none px-5 py-4 list-none flex items-center justify-between text-sm font-semibold text-[#0A2540]">
        <span>{open ? "Hide" : "Show"} table of contents · {items.length} sections</span>
        <span className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}>▾</span>
      </summary>
      <ol className="px-5 pb-5 space-y-2">
        {items.map((it) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              onClick={() => setOpen(false)}
              className="block text-sm text-[#374151] py-1 hover:text-[#0066FF]"
            >
              {it.text}
            </a>
          </li>
        ))}
      </ol>
    </details>
  );
}
